'use client';

import { useDotEnv } from '@repo/dot-env/client';
import { getGeolocationServiceUrl } from '@repo/dot-env/geolocation-service-url';
import { GetDirectionSchema, GetRouteSchema } from '@repo/schema/map';
import { useEffect, useState } from 'react';
import { decodePath } from './decode-path.ts';
import { encodePath } from './encode-path.ts';
import { getRoutePreviewPoint, type RoutePreviewPoint, type RoutePreviewStop } from './route-preview.ts';
const DirectionsResponseSchema = GetDirectionSchema.extend({
  routes: GetRouteSchema.array()
});

/** Kept outside the hook: the React Compiler cannot lower `throw` inside try/catch. */
async function fetchRoutePreview(serviceUrl: string, routeSegments: string[]) {
  const response = await fetch(`${serviceUrl}/google/directions`, {
    body: JSON.stringify({
      routes: routeSegments
    }),
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    method: 'POST'
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch route preview: ${response.status}`);
  }
  const data = DirectionsResponseSchema.parse(await response.json());
  return {
    encodedPaths: data.routes.map(route => encodePath(route.steps.flatMap(step => decodePath(step.polyline.points)))).filter(Boolean),
    totalDistance: data.total
  };
}
export const useRoutePreview = (stops: RoutePreviewStop[]) => {
  const {
    GEOLOCATION_SERVICE_URL
  } = useDotEnv();
  const [encodedPaths, setEncodedPaths] = useState<string[]>([]);
  const [totalDistance, setTotalDistance] = useState<number>();
  const points = stops.map(stop => getRoutePreviewPoint(stop)).filter((point): point is RoutePreviewPoint => point !== null);
  const routeSegments = points.map((point_0, index) => {
    const nextPoint = points[index + 1];
    if (!nextPoint) {
      return null;
    }
    return `${point_0.lat},${point_0.lng};${nextPoint.lat},${nextPoint.lng}`;
  }).filter((segment): segment is string => segment !== null);
  const routeSegmentsKey = routeSegments.join('|');
  useEffect(() => {
    let isCancelled = false;
    const fetchRoute = async () => {
      if (routeSegments.length === 0) {
        setEncodedPaths([]);
        setTotalDistance(undefined);
        return;
      }
      try {
        const route = await fetchRoutePreview(GEOLOCATION_SERVICE_URL ?? getGeolocationServiceUrl(), routeSegments);
        if (isCancelled) {
          return;
        }
        setEncodedPaths(route.encodedPaths);
        setTotalDistance(route.totalDistance);
      } catch (error) {
        if (isCancelled) {
          return;
        }
        console.error('Failed to fetch route preview', error);
        setEncodedPaths([]);
        setTotalDistance(undefined);
      }
    };
    void fetchRoute();
    return () => {
      isCancelled = true;
    };
  }, [GEOLOCATION_SERVICE_URL, routeSegmentsKey]);
  return {
    encodedPaths,
    points,
    totalDistance
  };
};